/* eslint-disable import/no-unresolved */
import { ButtonType } from '@aglenergy/design-system/dist/types/components/core/button/button.types';
import { CartFlagsService, ProductCategory, SessionStorageKeys } from '@aglenergy/shared-library';
import { Component, ComponentInterface, h, JSX, Prop } from '@stencil/core';
import { onRemoveCartItem } from 'app/bundle/store/actions/cartActions';
import { store } from 'app/shared/store/store';
import { AddressDetailModel, FeatureKey, ProductCatalogueContentFeature, ProductCatalogueProduct } from 'app/shared/models/index';
import {
  EligibilityValidation,
  EligibilityValidationSource,
  EligibilityValidationSourceWithPromoCodeDls,
  StoreKeys
} from 'app/shared/constants/index';
import { featureService, productService, tealiumAnalyticService } from 'app/shared/services/index';
import {
  getWithoutOfferStrip,
  mergeProductDetailsWithNbnCommonLinks,
  renderContentBelowCards
} from 'app/shared/utilities/helper-functions';
import { CommonLinks } from 'app/shared/components/common-links/common-links';
import { addressService } from 'app/shared/services/address/address.service';

@Component({
  tag: 'nbn-product-card',
  styleUrl: 'nbn-product-card.scss'
})
export class NbnProductCard implements ComponentInterface {
  @Prop() public products: ProductCatalogueProduct[];
  @Prop() public selectedAddress: AddressDetailModel;
  @Prop() public isPersonalisedProductsLoading: boolean;
  @Prop() public focusCardIndex: number;

  private cardIdPrefix = 'card';
  private submitButtonIdPrefix = 'nbnPlanCardSubmitButton';

  private getFeatureIcon(featureIconName?: string): JSX.Element {
    if (!featureIconName) {
      return null;
    }
    if (featureIconName.includes('.')) {
      return <dls-dynamic-icon iconPath={featureIconName} altText="" />;
    }
    // TODO: remove decorative icon selection after cleanup
    const iconNameInitial = featureIconName.charAt(0);
    return iconNameInitial >= 'a' && iconNameInitial <= 'l' ? (
      <dls-decorative-icon-set1 id="svg" size="sm" type={featureIconName as any} />
    ) : (
      <dls-decorative-icon-set2 id="svg" size="sm" type={featureIconName as any} />
    );
  }

  public getInCartSku(): string {
    const cartDetails = store.get(StoreKeys.cart);
    if (!cartDetails) {
      return null;
    }
    const cartService = new CartFlagsService(cartDetails);
    const inCartNbnProduct = cartService.getNbnProducts()?.[0];
    return inCartNbnProduct ? inCartNbnProduct.sku : null;
  }

  public onButtonClick(product: ProductCatalogueProduct, isProductInCart: boolean, planCardIndex: number): void {
    if (isProductInCart) {
      onRemoveCartItem(product.sku);
      tealiumAnalyticService.trackPlanSelectionData(product, ProductCategory.Nbn, 'Remove Plan');
    } else {
      !this.isPersonalisedProductsLoading && this.onSelectedProduct(product.sku, planCardIndex);
    }
  }

  public onSelectedProduct(sku: string, planCardIndex: number): void {
    store.set(StoreKeys.selectedSKU, sku);
    store.set(StoreKeys.promoModalTriggerElementId, `${this.submitButtonIdPrefix}${planCardIndex}`);

    const productDetailsFromSession = JSON.parse(window.sessionStorage.getItem(SessionStorageKeys.ProductDetailsSession));
    const differentiatedPricing =
      productDetailsFromSession.length > 0 ? productDetailsFromSession[0]?.response?.products[0]?.differentiatedPricing : null;
    const eligibilityValidation = differentiatedPricing?.eligibilityValidation;
    const eligibilityValidationSource = differentiatedPricing?.eligibilityValidationSource;
    const productCategory = store.get('productCategory') as ProductCategory;
    const addressEntered = store.get('address');

    if (
      eligibilityValidation === EligibilityValidation.SignupValidation &&
      Object.entries(EligibilityValidationSource)?.some(
        ([, value]) => value.toLowerCase() === (eligibilityValidationSource as string).toLowerCase()
      ) &&
      addressEntered
    ) {
      if (productCategory === ProductCategory.Nbn && addressService.convertAddressToString(addressEntered)?.length > 0) {
        const userPromoModalDls = Object.entries(EligibilityValidationSourceWithPromoCodeDls)?.some(
          ([, value]) => value.toLowerCase() === (eligibilityValidationSource as string).toLowerCase()
        );

        // This is for promo-dialog to open.
        const isPromoModalKey = userPromoModalDls ? StoreKeys.isPromoModalDlsOpen : StoreKeys.isPromoModalOpen;
        store.set(isPromoModalKey, true);
      }
    } else {
      productService.onSelectedProductForNbn(this.products, this.selectedAddress, sku);
    }
  }

  public render(): JSX.Element {
    const inCartSku = this.getInCartSku();

    const anyStripText = this.products?.some((product) => product.content.stripText);

    const comparisonContainerClass = 'comparison-container';
    const productCategory = store.get(StoreKeys.productCategory);
    const techType = (store.get(StoreKeys.productDetails) as any)?.serviceQualification?.nbn?.serviceType;
    const selectedPlanCategory = store.get(StoreKeys.selectedPlanCategory);
    let generalContentCategoryItems = store.get(StoreKeys.generalContent).categoryItems;
    const nbnCommonLinks = featureService.getFeatureStatus(FeatureKey.NBN_COMMON_LINKS_ENABLED)
      ? store.get(StoreKeys.nbnCommonLinks)
      : null;
    if (nbnCommonLinks) {
      generalContentCategoryItems = mergeProductDetailsWithNbnCommonLinks(nbnCommonLinks);
    }
    document.documentElement.style.setProperty('--scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`);
    return (
      <div>
        <div class={comparisonContainerClass} id="comparison-container">
          {this.products && (
            <carousel-buttons numberOfProducts={this.products.length} focusedCardIndex={this.focusCardIndex}>
              <div class="card-container" id="card-container">
                {this.products.map((product, index) => {
                  const isProductInCart = product.sku === inCartSku;
                  const buttonLabel = isProductInCart ? 'Remove plan' : 'Choose this plan';
                  const buttonType = isProductInCart ? 'secondary' : 'primary';
                  return this.nbnCardRender(isProductInCart, anyStripText, product, index, buttonType, buttonLabel);
                })}
              </div>
            </carousel-buttons>
          )}
        </div>
        {generalContentCategoryItems !== undefined && generalContentCategoryItems.length && (
          <CommonLinks
            generalContentCategoryItems={generalContentCategoryItems}
            productCategory={productCategory}
            selectedPlanCategory={selectedPlanCategory}
            techType={techType}
          />
        )}
        <div class="below-cards-message">{renderContentBelowCards(this.products)}</div>
      </div>
    );
  }

  private nbnCardRender(
    isProductInCart: boolean,
    anyStripText: boolean,
    product: ProductCatalogueProduct,
    index: number,
    buttonType: ButtonType,
    buttonLabel: string
  ): any {
    const displayToUser = product.qualified !== false;
    const productHeadline = product.content.productHeadline.replace(/nbn/gi, '<strong>nbn</strong>');
    const displayWithoutOfferStrip = getWithoutOfferStrip(product);
    const links = product.content.links;
    return (
      <div class="card__wrapper">
        <div
          class={`card scroll-snap ${isProductInCart ? 'card__plan-selected' : ''} ${!displayToUser ? 'unavailable' : ''}`}
          id={`${this.cardIdPrefix}${index}`}
        >
          <dls-card
            padding="md"
            elevationType="level1"
            radius="round02"
            stripColor={product.content.stripText ? 'secondary-04' : 'none'}
            stripText={product.content.stripText}
            topMargin={anyStripText && !product.content.stripText ? 'space04' : 'none'}
          >
            <div>
              <dls-h3 styledAs="title4">{product.content.productName}</dls-h3>
              <div class="product-headline" innerHTML={productHeadline} />
            </div>
            <div class="unavailable-overlay-container">
              <div class={!displayToUser && 'unavailable-overlay'} hidden={displayToUser}>
                <info-tag heading={product.content.unavailableHeading} content={product.content.unavailableContent} />
              </div>
              <div>
                <telco-new-price-tag
                  price={product.content.bundleOfferPrice || product.attributes.agl_marketing_price}
                  paymentFreq={product.content.priceVariable}
                  priceDesc={displayToUser ? product.content.bundleOfferText : 'Bundle Price ...'}
                  bundleTitle={product.content.bundleOfferStrip}
                />

                {displayWithoutOfferStrip && (
                  <telco-new-price-tag
                    class="without-offer-price-tag"
                    price={product.content.withoutOfferPrice || product.price}
                    paymentFreq={product.content.priceVariable}
                    priceDesc={product.content.withoutOfferText}
                  />
                )}
              </div>
              <div class="feature-list">
                {product.content?.features.map((feature: ProductCatalogueContentFeature) => {
                  return (
                    <telco-feature-text desc={feature.featureDescription}>{this.getFeatureIcon(feature.featureIcon)}</telco-feature-text>
                  );
                })}
              </div>
              <div class="cta--button">
                <dls-button
                  id={`${this.submitButtonIdPrefix}${index}`}
                  variant={buttonType}
                  loading={this.isPersonalisedProductsLoading || store.get('skuBeingRemoved') === product.sku}
                  data-testid="cta-main"
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={() => displayToUser && this.onButtonClick(product, isProductInCart, index)}
                >
                  {this.selectedAddress ? buttonLabel : 'Check your address'}
                </dls-button>
              </div>
              <div class="links">
                {links?.map((link) => {
                  return (
                    <div>
                      <dls-link href={link.url} open-in-new-window>
                        {link.title}
                      </dls-link>
                    </div>
                  );
                })}
              </div>
            </div>
          </dls-card>
        </div>
      </div>
    );
  }
}
